import { gql, useMutation } from '@apollo/client';
import { showToast } from '../components/common/CustomToast';
import { parkingVehicleLicensePlate } from '../states/common';
import {
  Mutation,
  MutationUpdatePermitArgs,
} from '../types/generatedSchemaTypes';
import { updateCache } from '../utils/cacheUtils';

type UpdatePermitOptions = {
  isRedeemable?: boolean;
  isPermitLoyaltyType?: boolean;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  currentRegistrationPlates?: { current: number };
};

/**
 * Returns the mutation options for updating a permit
 * @param permitId - The ID of the permit to update
 * @param options - Configuration options for the mutation
 */
export const getUpdatePermitMutationOptions = (
  permitId: string,
  options: UpdatePermitOptions = {}
) => {
  const {
    isRedeemable,
    isPermitLoyaltyType,
    onSuccess,
    onError,
    currentRegistrationPlates,
  } = options;

  return {
    update: (cache: any, { data }: any) => {
      parkingVehicleLicensePlate(undefined);
      if (permitId) {
        const id = permitId;
        const { registrationPlates } = data?.updatePermit || {};
        updateCache(
          cache,
          {
            __typename: 'UnifiedPermit',
            id,
            orderLineIdWithVersion: null,
          },
          {
            fragment: gql`
              fragment UnifiedPermitFragment on UnifiedPermit {
                registrationPlates
                isWaitingActivation
              }
            `,
            data: {
              registrationPlates,
              isWaitingActivation: !registrationPlates?.length,
            },
          }
        );
      }
    },
    onCompleted: (data: any) => {
      if (!isRedeemable || currentRegistrationPlates?.current) {
        showToast(
          isPermitLoyaltyType
            ? 'success.loyaltyMembershipUpdated'
            : 'success.updatePermit',
          'success'
        );
      } else {
        showToast(
          isPermitLoyaltyType
            ? 'success.loyaltyMembershipRedeemed'
            : 'success.permitRedeem',
          'success'
        );
      }
      if (currentRegistrationPlates) {
        currentRegistrationPlates.current =
          data.updatePermit?.registrationPlates?.length || 0;
      }
      onSuccess?.(data);
    },
    onError: () => {
      if (isRedeemable) {
        showToast('error.permitRedeemFailed', 'error');
      } else {
        showToast('error.permitUpdateFailed', 'error');
      }
      onError?.();
    },
  };
};

/**
 * Custom hook for using the UPDATE_PERMIT mutation
 * @param permitId - The ID of the permit to update
 * @param options - Configuration options for the mutation
 * @returns The mutation function and loading state
 */
const useUpdatePermit = (
  permitId: string,
  options: UpdatePermitOptions = {}
) => {
  const UPDATE_PERMIT_QUERY = gql`
    mutation updatePermit($input: UpdatePermitInput!) {
      updatePermit(input: $input) {
        id
        registrationPlates {
          plateText
          countryCode
        }
      }
    }
  `;

  const [updatePermit, { loading }] = useMutation<
    Mutation,
    MutationUpdatePermitArgs
  >(UPDATE_PERMIT_QUERY, getUpdatePermitMutationOptions(permitId, options));

  return { updatePermit, loading };
};

export default useUpdatePermit;
